/*
 * Licensed under the Apache License, Version 2.0 (the "License"); you may not use
 * this file except in compliance with the License. You may obtain a copy of the
 * License at
 *
 *  https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed
 * under the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR
 * CONDITIONS OF ANY KIND, either express or implied. See the License for the
 * specific language governing permissions and limitations under the License.
 */

import { noop } from "lodash";
import { useMemo } from "react";
import useSWR, { useSWRInfinite } from "swr";

import { BaseAPIService, metaDeserializer } from "../services/APIBase";
import { withFetchErrorTracking } from "../services/Telemetry";
import { IQueryMany, IResponseMany, IResponseMultiple } from "../interfaces";
import { encodeQueryToURL } from "../utils";

interface IOptions {
  fetcher?: (url: string) => Promise<any>;
  transformResponse?: (response: any) => any;
  pageSize?: number;
  swrOptions?: {
    [key: string]: any;
  };
}

// The first page always carries the facets (`filters`) and the totals (`results`,
// and `datasetResults` where the endpoint reports one) — some endpoints (e.g.
// layers) only return facets on that page, later pages send `filters: []` — while
// the next-page cursor advances with the last page. So take the first-page-only
// fields from the first page and everything else from the last.
export const combineListMeta = (rawData: any[]): any => {
  const firstMeta = rawData[0]?.meta || {};
  const lastMeta = rawData[rawData.length - 1]?.meta || {};
  return {
    ...lastMeta,
    filters: firstMeta.filters ?? lastMeta.filters,
    results: firstMeta.results ?? lastMeta.results,
    datasetResults: firstMeta.datasetResults ?? lastMeta.datasetResults,
  };
};

const defaultOptions = {
  fetcher: (url) => BaseAPIService.request(url, undefined, metaDeserializer),
  transformResponse: (response) => response,
  pageSize: 30,
};

export function useFetchPaginated(
  url: string,
  query: IQueryMany,
  passedOptions?: IOptions
): IResponseMultiple {
  const options = {
    ...defaultOptions,
    ...passedOptions,
    // set explicitly: the spread above is shallow, so a call site passing
    // swrOptions would otherwise drop error reporting entirely
    swrOptions: withFetchErrorTracking(passedOptions?.swrOptions, url),
  };

  const encodedQuery = encodeQueryToURL(url, query);

  const fetcher = (url) => {
    return options.fetcher(url).then(options.transformResponse);
  };

  return useSWR(encodedQuery, fetcher, options.swrOptions);
}

// TODO: rename to useFetchInfinite
export function useFetchMany(
  url: string,
  query: IQueryMany,
  passedOptions?: IOptions
): IResponseMany {
  const options = {
    ...defaultOptions,
    ...passedOptions,
    // set explicitly: the spread above is shallow, so a call site passing
    // swrOptions would otherwise drop error reporting entirely
    swrOptions: withFetchErrorTracking(passedOptions?.swrOptions, url),
  };
  const swrKeyLoader = (pageIndex: number, previousPage: any): string => {
    // skip data fetching
    if (!query) {
      return null;
    }

    // reached the end;
    if (previousPage && !previousPage.data) {
      return null;
    }
    const cursor =
      pageIndex === 0 ? -1 : previousPage?.meta?.pagination?.nextCursor;
    const defaults = {
      page: { size: defaultOptions.pageSize, cursor },
    };
    const finalQuery = {
      ...defaults,
      ...query,
      page: { ...defaults.page, ...query.page },
    };

    return encodeQueryToURL(url, finalQuery);
  };

  const fetcher = (url) => {
    return options.fetcher(url).then(options.transformResponse);
  };

  const response = useSWRInfinite(swrKeyLoader, fetcher, options.swrOptions);

  const { data: rawData, isValidating, size, setSize } = response;

  // Keep a stable `meta` reference while the pages don't change — consumers use it
  // as an effect dependency, so a fresh object each render would loop them.
  const meta = useMemo(
    () => (rawData ? combineListMeta(rawData) : {}),
    [rawData]
  );

  if (!rawData) {
    return response;
  }

  // combine array of pages in a single list of results
  const flatData = rawData.map((item) => item.data).flat();
  const isNoMore = !meta.pagination?.nextCursor;
  const awaitMore = !isValidating && !isNoMore;

  return {
    ...response,
    data: flatData,
    meta,
    pageSize: options.pageSize,
    isNoMore,
    awaitMore,
    nextPage: awaitMore ? () => setSize(size + 1) : noop,
    previousPage: size === 0 ? noop : () => setSize(size - 1),
    resetPage: () => setSize(1),
  };
}
